import { Dialog, DialogTitle, Typography, DialogContent, DialogActions, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Loader } from '../../../components/ui/Loader';
import { StockCheckItem } from './../../../app/services/api/generated';
import { applyRoundAndFormat } from './../../../utils';
import StockCheckCategoryTable from './StockCheckCategoryTable';
import { TempStockCheckItem } from '..';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { CloseDialogAction } from './../../../components/shared/dialogs/CloseDialogAction';

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    isApproving: boolean;
    maxToleranceWarningValue?: number;
    itemsAboveMaxTolerance: StockCheckItem[];
    setItemsAboveMaxTolerance: Dispatch<SetStateAction<StockCheckItem[]>>;
    onItemStatusChange: (itemId: number | undefined, checked: boolean) => void;
    currencyCode: string;
}

const StockCheckApproveDialog = ({ open, onClose, onSubmit, isApproving, maxToleranceWarningValue, itemsAboveMaxTolerance, setItemsAboveMaxTolerance, onItemStatusChange, currencyCode }: Props) => {
  const { t } = useTranslation('stockCountReview');

  const isAnyItemsInvalid = useCallback(() => {
    return itemsAboveMaxTolerance.some(item => {
      return item.isApproved !== true && item.isApproved !== false;
    });
  }, [itemsAboveMaxTolerance]);

  const handleItemStatusChange = useCallback((itemId: number | undefined, checked: boolean) => {
    // Update local state immediately for the dialog
    setItemsAboveMaxTolerance(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, isApproved: checked }
          : item,
      ),
    );

    onItemStatusChange(itemId, checked);
  }, [setItemsAboveMaxTolerance, onItemStatusChange]);

  const maxWidth = useMemo(() => itemsAboveMaxTolerance.length > 0 ? 'lg' : 'sm', [itemsAboveMaxTolerance.length]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
    >
      <DialogTitle>
        {t('approvalDialogTitle')}
        <CloseDialogAction onCancel={onClose} />
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant='textMD'
          sx={{ my: 4, mx: 6 }}>
          {
            itemsAboveMaxTolerance.length > 0 ? t('approvalDialogWarning', { value: applyRoundAndFormat(maxToleranceWarningValue ?? 0, currencyCode) }) : t('approvalDialogMessage')
          }
        </Typography>
        {
          itemsAboveMaxTolerance.length > 0 &&
                    <Box sx={{ p: '10px' }}>
                      <StockCheckCategoryTable
                        category={{ name: '', items: itemsAboveMaxTolerance as TempStockCheckItem[] }}
                        categoryIndex={0}
                        sortPartialStockCountItems={() => { }}
                        togglePartialStockCountItem={() => { }}
                        onItemStatusChange={handleItemStatusChange}
                        isDisabled={false}
                        isOpenedInDialog
                        currencyCode={currencyCode}
                      />
                    </Box>
        }
      </DialogContent>
      <DialogActions sx={{ mt: 4, mb: 4, mr: 4 }}>
        <Button
          variant="secondary"
          onClick={onClose}
          autoFocus
        >
          {t('close')}
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isApproving || isAnyItemsInvalid()}
          startIcon={isApproving && <Loader />}
          autoFocus
        >
          {isApproving ? t('isConfirming') : t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default StockCheckApproveDialog;